import { EventNotOnline } from "@ac-expo/ui-components";
import { DehydratedState, HydrationBoundary } from "@tanstack/react-query";
import { prefetchEventBySlug } from "@web-app/src/server/prefetchEventBySlug";
import { GetServerSideProps } from "next";
import CustomHead from "../../components/CustomHead";
import ProtectedEventDetail from "../../components/Event/ProtectedEventDetail";

export const getServerSideProps: GetServerSideProps<EventPageProps> = async ({
  params,
  res,
}) => {
  const slug = params?.slug as string;

  if (!slug) {
    return {
      notFound: true,
    };
  }

  const { dehydratedState, event } = await prefetchEventBySlug(slug, res);

  // Only online events are public. Decide here, on the server: a redirect
  // from inside the component tree has no router during SSR and throws.
  // An unknown slug (event === null) falls through to the client tree,
  // which renders EventNotFound with the 404 set by prefetchEventBySlug.
  const showNotOnlinePage = event !== null && event.status !== "online";

  if (showNotOnlinePage) {
    res.statusCode = 404;
  }

  return {
    props: {
      dehydratedState,
      slug,
      showNotOnlinePage,
    },
  };
};

interface EventPageProps {
  dehydratedState: DehydratedState;
  slug: string;
  showNotOnlinePage: boolean;
}

export default function EventPage({
  dehydratedState,
  slug,
  showNotOnlinePage,
}: EventPageProps) {
  if (showNotOnlinePage) {
    return (
      <>
        <CustomHead
          title="Événement pas encore en ligne - Connect'Events"
          content="Cet événement n'est pas encore en ligne."
          pageSlug={`/event/${slug}`}
        />
        <EventNotOnline />
      </>
    );
  }

  return (
    <HydrationBoundary state={dehydratedState}>
      <ProtectedEventDetail eventSlug={slug} />
    </HydrationBoundary>
  );
}
